<template>
  <div class="kit-material-workbench ord-suite-view" v-loading="initialLoading">
    <!-- 顶部操作条 -->
    <section v-if="!toolbarInTabs" class="query-band" aria-label="订单操作条">
      <nav class="dimension-nav" aria-label="订单操作条">
        <div class="dimension-tabs"></div>
        <div class="dimension-nav-actions">
          <el-button class="shadcn-button shadcn-button--ghost" size="mini" icon="el-icon-refresh" :loading="suiteLoading" @click="loadSuiteData(true)">刷新</el-button>
        </div>
      </nav>
    </section>

    <!-- 上栏：订单齐套汇总表格 -->
    <section class="pane upper-pane" :style="{ height: upperHeight + 'px' }" aria-label="订单齐套汇总">
      <div class="table-wrap" v-loading="suiteLoading || summaryFieldLoading">
        <ve-table
          ref="summaryTable"
          class="summary-table custom-table-border"
          :columns="summaryVeColumns"
          :table-data="filteredSummary"
          :border-around="true"
          :border-x="true"
          :border-y="true"
          :fixed-header="true"
          :scroll-width="0"
          row-key-field-name="rowKey"
          :row-style-option="summaryRowStyleOption"
          :cell-style-option="summaryCellStyleOption"
          :event-custom-option="summaryEventCustomOption"
          :column-width-resize-option="summaryColumnWidthResizeOption"
          :cellSelectionOption="{ enable: false }"
          style="width: 100%; height: 100%;"
        />
      </div>
    </section>

    <div v-if="summaryFieldError" class="material-query-error" role="alert">
      <span>{{ summaryFieldError }}</span>
      <el-button type="text" size="mini" :loading="summaryFieldLoading" @click="getTabField">重试订单字段加载</el-button>
    </div>
    <div v-if="suiteError" class="material-query-error" role="alert">{{ suiteError }}</div>
    <!-- 分割拖拽条 -->
    <div class="splitter" title="拖动调整上下表格高度" @mousedown="startResize"></div>

    <!-- 下栏：物料齐套明细工作区 -->
    <section class="pane lower-pane" aria-label="物料齐套明细">
      <!-- 物料操作栏 -->
      <div class="material-workbar">
        <strong class="material-section-title">请购明细</strong>
        <div class="selected-sku-context" :title="selectedOrderLabel">{{ selectedOrderLabel || '未选择订单' }}</div>

        <el-input v-model="materialSearch" class="material-quick-search" size="mini" clearable placeholder="物料编码 / 物料名称 / 数量" @input="handleMaterialSearch" @clear="commitMaterialSearch"
          @keyup.enter.native="commitMaterialSearch" />
        <div class="material-state-filter" data-ui="toggle-group" role="group" aria-label="物料状态筛选">
          <button type="button" class="state-filter-button" :class="{ 'is-active': materialStateFilter === 'all' }" @click="setMaterialStateFilter('all')">全部 {{ searchedMaterials.length }}</button>
          <button v-for="state in materialStatuses" :key="state" type="button" class="state-filter-button"
            :class="[postStatusClass(state), { 'is-active': materialStateFilter === state }]"
            @click="setMaterialStateFilter(state)">{{ state }} {{ materialStateCounts[state] || 0 }}</button>
        </div>

        <div class="selection-actions">
          <el-button class="shadcn-button shadcn-button--outline" size="mini" title="追踪" :disabled="selectedMaterials.length === 0" @click="openSelectedLocation">
            <span class="selection-action-label">追踪</span>
          </el-button>
        </div>
      </div>

      <div v-if="materialQueryError" class="material-query-error" role="alert">
        <span>{{ materialQueryError }}</span>
        <el-button type="text" size="mini" icon="el-icon-refresh" @click="refreshSelectedMaterials()">重试</el-button>
      </div>
      <div v-if="materialFieldError" class="material-query-error" role="alert">
        <span>{{ materialFieldError }}</span>
        <el-button type="text" size="mini" :loading="materialFieldLoading" @click="loadMaterialFieldMetadata">重试请购字段加载</el-button>
      </div>

      <!-- 请购明细按原始记录展示，状态只读 POSTFG -->
      <div class="table-wrap" v-loading="materialQueryLoading || materialFieldLoading">
        <ve-table
          ref="netMaterialTable"
          class="material-table custom-table-border"
          :columns="materialVeColumns"
          :table-data="filteredNetMaterials"
          :border-around="true"
          :border-x="true"
          :border-y="true"
          :fixed-header="true"
          :scroll-width="0"
          row-key-field-name="rowKey"
          :cell-style-option="materialCellStyleOption"
          :event-custom-option="materialEventCustomOption"
          :column-width-resize-option="materialColumnWidthResizeOption"
          :cellSelectionOption="{ enable: false }"
          style="width: 100%; height: 100%;"
        />
        <div v-if="materialEmptyText" class="material-empty-state" role="status" aria-live="polite">
          <i class="el-icon-document" aria-hidden="true"></i>
          <span>{{ materialEmptyText }}</span>
        </div>
      </div>
    </section>

    <!-- 弹窗：物料仓存锁库与释放 (内置自包含) -->
    <el-dialog
      title="物料仓存锁库与释放"
      custom-class="kit-dialog material-lock-dialog"
      :visible.sync="erpLockDialogVisible"
      width="min(1120px, 96vw)"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="!erpLockWriting"
      :show-close="!erpLockWriting" @close="closeLockDialog">
      <div class="material-lock-content-scroll">
        <!-- 待锁物料列表 -->
        <section aria-label="所选锁库物料" class="material-lock-section">
          <el-table :data="erpLockContexts" border size="mini" :row-key="lockContextRowKey" highlight-current-row @row-click="handleErpLockContextClick">
            <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
            <el-table-column prop="matNo" label="物料编码" width="112"></el-table-column>
            <el-table-column prop="matName" label="物料名称" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column prop="demandQty" label="需求数量" width="86" align="right"></el-table-column>
            <el-table-column prop="availableQty" label="当前可用量" width="96" align="right">
              <template slot-scope="scope"><strong class="success-text">{{ scope.row.availableQty }}</strong></template>
            </el-table-column>
            <el-table-column prop="lockedQty" label="累计已锁" width="86" align="right"></el-table-column>
            <el-table-column prop="remainingQty" label="剩余待锁" width="86" align="right"></el-table-column>
          </el-table>
        </section>

        <!-- 仓库可用库存分配 -->
        <section aria-label="可用库存" class="material-lock-section">
          <div class="material-lock-section-head">
            <div><strong>可用库存</strong><span>（{{ activeErpLockContext ? activeErpLockContext.matNo : '' }}）仓库可用库存分配</span></div>
            <el-button type="text" size="mini" icon="el-icon-refresh" :disabled="erpLockWriting" @click="refreshActiveErpLockStock">刷新库存</el-button>
          </div>
          <el-table v-loading="erpLockLoading" :data="erpLockStockRows" border size="mini" max-height="200" empty-text="暂无可用库存">
            <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
            <el-table-column prop="STKNM" label="仓库名称" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="LOTNO" label="批次号" min-width="108" show-overflow-tooltip></el-table-column>
            <el-table-column prop="QTY" label="库存现存量" width="96" align="right"></el-table-column>
            <el-table-column prop="OKQTY" label="当前可用量" width="96" align="right">
              <template slot-scope="scope"><strong class="success-text">{{ scope.row.OKQTY }}</strong></template>
            </el-table-column>
            <el-table-column label="分配数量" width="140" align="center">
              <template slot-scope="scope">
                <el-input v-model.number="scope.row.ALLOQTY" size="mini" type="number" :min="0" :max="scope.row.OKQTY" :disabled="erpLockWriting" placeholder="输入数量"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="82" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="mini" icon="el-icon-lock" :disabled="erpLockWriting || !scope.row.ALLOQTY || scope.row.ALLOQTY <= 0" @click="allocateErpLock(scope.row)">分配</el-button>
              </template>
            </el-table-column>
          </el-table>
        </section>

        <!-- 已锁库明细 -->
        <section aria-label="已锁库明细" class="material-lock-section">
          <div class="material-lock-section-head">
            <div><strong>已锁库明细</strong><span>当前需求行已锁定的仓存明细</span></div>
          </div>
          <el-table v-loading="erpLockLoading" :data="erpLockLockedRows" border size="mini" max-height="180" empty-text="暂无锁库明细">
            <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
            <el-table-column prop="STKNM" label="锁定仓库" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="LOTNO" label="锁定批次" min-width="108" show-overflow-tooltip></el-table-column>
            <el-table-column prop="QTY" label="锁定数量" width="96" align="right"></el-table-column>
            <el-table-column prop="ADDUSER" label="锁定人" width="100" show-overflow-tooltip></el-table-column>
            <el-table-column prop="ADDDTTM" label="锁定时间" min-width="146" show-overflow-tooltip></el-table-column>
            <el-table-column label="操作" width="92" align="center">
              <template slot-scope="scope">
                <el-button class="danger-text" type="text" size="mini" icon="el-icon-unlock" :disabled="erpLockWriting" @click="releaseErpLock(scope.row)">整条释放</el-button>
              </template>
            </el-table-column>
          </el-table>
        </section>
      </div>
      <span slot="footer">
        <el-button class="shadcn-button shadcn-button--outline" size="small" :disabled="erpLockWriting" @click="erpLockDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 抽屉：物料位置状态与供给追踪 -->
    <el-drawer
      :title="locationTitle"
      custom-class="kit-drawer"
      :visible.sync="locationDrawer"
      append-to-body
      :size="locationMode === 'multiple' ? 'min(960px, 100vw)' : 'min(640px, 100vw)'"
      @close="invalidateSupplyTrace">
      <div class="drawer-body" v-loading="traceLoading">
        <div class="trace-actions"><span>{{ selectedOrderLabel }}</span><el-button size="mini" icon="el-icon-refresh" :disabled="traceLoading || materialQueryLoading" @click="reloadSupplyTrace">刷新库存</el-button></div>
        <div v-if="traceError" class="material-query-error" role="alert">{{ traceError }}</div>
        <div class="trace-statuses"><span v-for="state in activeLocationStatuses" :key="state" :class="postStatusClass(state)">{{ displayValue(state) }}</span></div>
        <div v-if="locationMode === 'multiple'" class="drawer-section">
          <h3 class="drawer-heading">物料最新情况</h3>
          <el-table :data="locationOverviewRows" border size="mini" :row-key="locationOverviewRowKey">
            <el-table-column type="index" label="序号" width="50" fixed align="center"></el-table-column>
            <el-table-column prop="matNo" label="物料编码" width="108" fixed show-overflow-tooltip></el-table-column>
            <el-table-column prop="matName" label="物料名称" min-width="126" fixed show-overflow-tooltip></el-table-column>
            <el-table-column prop="unit" label="单位" width="70"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="availableQty" label="可用库存" width="78" align="right"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="inTransit" label="在途" width="66" align="right"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="allocatedQty" label="订单占用" width="78" align="right"></el-table-column>
            <el-table-column label="最新状态" min-width="140">
              <template slot-scope="scope"><span v-for="state in scope.row.statuses" :key="state" class="trace-status-label" :class="postStatusClass(state)">{{ displayValue(state) }}</span></template>
            </el-table-column>
            <el-table-column label="操作" width="66" fixed="right" align="center">
              <template slot-scope="scope"><el-button class="shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-view" @click="openLocationDetail(scope.row)">详情</el-button></template>
            </el-table-column>
          </el-table>
        </div>
        <template v-else>
          <div class="drawer-section">
            <h3 class="drawer-heading">实时库存</h3>
            <el-table :data="activeMaterialLocations" border size="mini" row-key="rowKey" empty-text="暂无分仓库存记录">
              <el-table-column type="index" label="序号" width="46" align="center"></el-table-column>
              <el-table-column prop="stkName" label="仓库名称" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="unit" label="单位" width="65"></el-table-column>
              <el-table-column :formatter="formatSummaryCell" prop="qty" label="物理库存" width="82" align="right"></el-table-column>
              <el-table-column :formatter="formatSummaryCell" prop="okQty" label="可用库存" width="82" align="right">
                <template slot-scope="scope"><strong class="success-text">{{ displayValue(scope.row.okQty) }}</strong></template>
              </el-table-column>
            </el-table>
          </div>
          <div class="drawer-section">
            <h3 class="drawer-heading">订单采购/请购在途明细</h3>
            <el-table :data="activeMaterialPoItems" border size="mini" row-key="rowKey" :row-class-name="materialRowClassName" empty-text="暂无在途采购单">
              <el-table-column type="index" label="序号" width="46" align="center"></el-table-column>
              <el-table-column prop="poNo" label="采购单号" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="supplier" label="供应商" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column :formatter="formatSummaryCell" prop="qty" label="请购数" width="70" align="right"></el-table-column>
              <el-table-column :formatter="formatSummaryCell" prop="inTransit" label="在途数" width="70" align="right">
                <template slot-scope="scope"><span>{{ displayValue(scope.row.inTransit) }}</span></template>
              </el-table-column>
              <el-table-column prop="unit" label="单位" width="65"></el-table-column>
              <el-table-column prop="POSTFG" label="状态" width="90" :formatter="formatSummaryCell"></el-table-column>
              <el-table-column prop="replyDate" label="回复交期" width="100"></el-table-column>
            </el-table>
          </div>
        </template>
      </div>
    </el-drawer>

    <!-- 抽屉：单物料供给详情二级抽屉 -->
    <el-drawer :title="locationDetailTitle" custom-class="kit-drawer" :visible.sync="locationDetailDrawer" append-to-body :size="'min(640px, 100vw)'">
      <div class="drawer-body" v-loading="traceLoading">
        <div class="trace-statuses"><span v-for="state in activeDetailStatuses" :key="state" :class="postStatusClass(state)">{{ displayValue(state) }}</span></div>
        <div class="drawer-section">
          <h3 class="drawer-heading">分仓库存分布</h3>
          <el-table :data="activeDetailLocations" border size="mini" row-key="rowKey" empty-text="暂无分仓记录">
            <el-table-column type="index" label="序号" width="46" align="center"></el-table-column>
            <el-table-column prop="stkName" label="仓库名称" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="unit" label="单位" width="65"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="qty" label="物理库存" width="82" align="right"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="okQty" label="可用库存" width="82" align="right"></el-table-column>
          </el-table>
        </div>
        <div class="drawer-section">
          <h3 class="drawer-heading">采购在途明细</h3>
          <el-table :data="activeDetailPoItems" border size="mini" row-key="rowKey" :row-class-name="materialRowClassName" empty-text="暂无在途记录">
            <el-table-column type="index" label="序号" width="46" align="center"></el-table-column>
            <el-table-column prop="poNo" label="采购单号" min-width="120"></el-table-column>
            <el-table-column prop="supplier" label="供应商" min-width="120"></el-table-column>
            <el-table-column prop="qty" label="数量" width="80" :formatter="formatSummaryCell"></el-table-column>
            <el-table-column prop="unit" label="单位" width="65"></el-table-column>
            <el-table-column prop="POSTFG" label="状态" width="90" :formatter="formatSummaryCell"></el-table-column>
            <el-table-column :formatter="formatSummaryCell" prop="inTransit" label="在途数" width="70" align="right"></el-table-column>
            <el-table-column prop="replyDate" label="回复交期" width="100"></el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>

    <el-dialog v-if="materialRemarkVisible" custom-class="suite-remark-dialog" title="跟进信息"
      :visible.sync="materialRemarkVisible" width="min(980px, 94vw)" append-to-body
      :close-on-click-modal="false" :close-on-press-escape="false" top="8vh">
      <div class="suite-remark-material">
        <span class="suite-remark-code">{{ materialRemarkRow.MATNO }}</span>
        <strong>{{ materialRemarkRow.MATNM }}</strong>
      </div>
      <div class="suite-remark-section"><span>跟进记录</span><span>{{ materialRemarkRows.length }} 条</span></div>
      <div v-loading="materialRemarkLoading" class="suite-remark-content">
        <el-alert v-if="materialRemarkError" :title="materialRemarkError" type="error" :closable="false" show-icon />
        <el-button v-if="materialRemarkError" type="text" @click="loadMaterialRemarks">重新加载</el-button>
        <el-table v-else :data="materialRemarkRows" border size="small" empty-text="暂无跟进记录" :max-height="420">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column v-for="field in materialRemarkFields" :key="field.key" :prop="field.key"
            :label="field.title" :min-width="field.key === 'RMK' ? 300 : (Number(field.width) || 130)">
            <template slot-scope="scope"><span class="suite-remark-value" :class="{ 'suite-remark-note': field.key === 'RMK' }">{{ displayValue(scope.row[field.key]) }}</span></template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer"><el-button size="small" @click="materialRemarkVisible = false">关闭</el-button></div>
    </el-dialog>
  </div>
</template>

<script>
import { getTabColOrData } from '@/api/user';
import { mergeFieldSettings } from '@/utils/formFieldSettingsService';

const EMPTY_ROWS = Object.freeze([]);
// 使用本页专用灰色状态类，避免全局 table-body-cell-class1 同时覆盖单元格边框。
const POST_STATUS = Object.freeze({
  '未开始': 'ord-suite-status-pending', '进行中': 'table-body-cell-blue',
  '已完成': 'table-body-cell-green', '异常': 'table-body-cell-yellow', '加急': 'table-body-cell-red'
});
function freezeSnapshot(value, seen = new WeakSet()) {
  if (!value || typeof value !== 'object' || seen.has(value) || Object.isFrozen(value)) return value;
  seen.add(value);
  Object.keys(value).forEach(key => freezeSnapshot(value[key], seen));
  return Object.freeze(value);
}
function firstOptionalNumber(row, keys) {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return numberOrNull(row[key]);
  }
  return null;
}
function unitOf(row) { return text(row.UNITNM || row.UNIT || row.unit); }
function sumKnown(rows, key) {
  let total = 0;
  for (const row of rows) {
    const value = numberOrNull(row[key]);
    if (value == null) return null;
    total += value;
  }
  return total;
}
// 只增加内部行键，不补充业务字段、别名或推算值。
function normalizeRequestRows(rows) {
  const keys = new Set();
  return rows.map((raw, index) => {
    const key = text(raw.PKID) || [raw.IDSEQ, raw.LSTSEQ, raw.MATNO, index].join('::');
    if (keys.has(key)) throw new Error('请购明细主键重复，无法可靠选择');
    keys.add(key);
    return Object.assign({}, raw, { rowKey: key });
  });
}
function buildSupplyGroups(target, stockResult, requestRows) {
  const stocks = stockResult.rows, purchases = requestRows;
  const units = new Set(stocks.concat(purchases).map(unitOf));
  if (!units.size) (target.units.length ? target.units : ['']).forEach(unit => units.add(unit));
  return Array.from(units).map(unit => {
    const locations = stocks.filter(row => unitOf(row) === unit).map((raw, index) => Object.assign({}, raw, {
      rowKey: [target.matNo, unit, raw.IDSEQ, raw.LSTSEQ, index].join('::'),
      stkName: text(raw.STKNM), unit,
      qty: numberOrNull(raw.QTY), okQty: numberOrNull(raw.OKQTY)
    }));
    const poRows = purchases.filter(row => unitOf(row) === unit);
    const items = poRows.map((raw, index) => Object.assign({}, raw, {
      rowKey: text(raw.PKID) || [target.matNo, unit, raw.IDSEQ, raw.LSTSEQ, index].join('::'),
      poNo: text(raw.PONO), supplier: text(raw.SUPNM), unit,
      qty: numberOrNull(raw.QTY), inTransit: numberOrNull(raw.INTRANSIT), replyDate: text(raw.REPLYDTTM)
    }));
    return {
      rowKey: target.matNo + '::' + unit, matNo: target.matNo, matName: target.matName, unit,
      availableQty: stockResult.error ? null : sumKnown(locations, 'OKQTY'),
      inTransit: sumKnown(poRows, 'INTRANSIT'),
      allocatedQty: sumKnown(poRows, 'LOCKQTY'),
      statuses: Array.from(new Set(poRows.map(row => text(row.POSTFG)))),
      locations, poItems: items, error: stockResult.error
    };
  });
}
async function mapWithLimit(rows, worker) {
  const result = new Array(rows.length);
  let cursor = 0;
  await Promise.all(Array.from({ length: Math.min(4, rows.length) }, async () => {
    while (cursor < rows.length) { const index = cursor++; result[index] = await worker(rows[index]); }
  }));
  return result;
}

// 基础数据转换
// 将接口字段统一转换为去除首尾空格的字符串。
function text(value) {
  return value == null ? '' : String(value).trim();
}

// 将输入转换为有限数值，非法值使用指定默认值。
function number(value, fallback) {
  const result = Number(value);
  return Number.isFinite(result) ? result : (fallback == null ? 0 : fallback);
}

// 将可选数值转换为 Number，空值或非法值返回 null。
function numberOrNull(value) {
  if (value === '' || value == null) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

// 增加内部唯一行键，业务列直接读取 V_ORD_SUITE 的原始字段。
function filterSuiteRows(rows, corpid) {
  return (rows || []).filter(row => !row.CORPID || text(row.CORPID) === text(corpid)).map((row, idx) => {
    const cid = text(row.CORPID || corpid);
    const idseq = text(row.IDSEQ || row.idseq);
    const lstseq = text(row.LSTSEQ || row.lstseq);
    const rowKey = [cid, idseq, lstseq].filter(Boolean).join('::') || `ORD_ROW_${idx}`;
    return Object.assign({}, row, { rowKey });
  });
}

// 按元数据定义处理表头条件，不把字段名改为页面别名。
function filterColumnRows(rows, fields, filters) {
  const active = fields.filter(field => {
    const value = filters[field.key];
    return field.key !== 'SEL' && value !== undefined && value !== null && value !== '';
  });
  if (!active.length) return rows;
  return rows.filter(row => active.every(field => {
    const value = row[field.key], query = filters[field.key];
    if (field.EDCSS === 'number') {
      const left = numberOrNull(value), right = numberOrNull(query);
      if (left == null || right == null) return false;
      const oper = filters['#OPER' + field.key] || '=';
      if (oper === '>') return left > right;
      if (oper === '<') return left < right;
      if (oper === '!=') return left !== right;
      return left === right;
    }
    if (field.EDCSS === 'checkbox' || field.key === 'STFG' || field.key === 'POSTFG') return String(value) === String(query);
    if (field.EDCSS === 'date' || field.EDCSS === 'datetime') return text(value).slice(0, 10) === text(query);
    return text(value).toLowerCase().includes(text(query).toLowerCase());
  }));
}

// 提取接口返回的列表行数据，校验 code 200 与数组格式。
function extractRows(res, defaultMsg) {
  if (res && String(res.code) === '200' && Array.isArray(res.result)) {
    return res.result;
  }
  const msg = (res && (typeof res.result === 'string' ? res.result : res.message)) || defaultMsg || '读取数据失败';
  throw new Error(msg);
}

export default {
  name: 'OrdSuite',
  props: { toolbarInTabs: { type: Boolean, default: false } },
  // 声明订单、物料、弹窗及请求生命周期状态。
  data() {
    return {
      materialRemarkVisible: false,
      materialRemarkRow: {},
      materialRemarkRows: [],
      materialRemarkFields: [],
      materialRemarkLoading: false,
      materialRemarkError: '',
      materialRemarkRequestId: 0,
      initialLoading: true,
      materialStatuses: Object.keys(POST_STATUS),
      traceLoading: false,
      traceError: '',
      traceMaterialTargets: [],
      activeLocationStatuses: [],
      activeDetailStatuses: [],
      activeDetailKey: '',
      appliedSummaryFilters: {},
      orderStatusOptions: [
        { value: 'Y', label: this.$LANG('1976', '已审核') },
        { value: 'P', label: this.$LANG('1961', '审核中') },
        { value: 'K', label: this.$LANG('1982', '已确认') },
        { value: 'N', label: this.$LANG('2005', '未审核') }
      ],
      checkOptions: [
        { value: 'Y', label: this.$LANG('2047', '是') },
        { value: 'N', label: this.$LANG('2044', '否') }
      ],
      finishOptions: [
        { value: 'Y', label: this.$LANG('2047', '是') },
        { value: 'N', label: this.$LANG('2044', '否') },
        { value: 'A', label: this.$LANG('2141', '关闭') }
      ],
      // 当前用户和账套请求上下文。
      v_url: 'xuper_auth/getBaseData',
      v_username: '',
      v_corpid: '',
      // 上下工作区布局及当前物料视图。
      upperHeight: 286,
      materialStateFilter: 'all',
      // 订单原始数据和订单选择状态。
      orders: [],
      selectedSummary: null,
      // 当前请购明细选择状态。
      selectedMaterials: [],
      selectedMaterialKeys: [],
      // 当前订单请购明细。
      materialNetRows: [],
      // 请购明细查询状态。
      materialQueryRequestId: 0,
      materialQueryLoading: false,
      materialQueryError: '',
      // 上表采用订单字段配置 490050/2，下表采用物料字段配置 490050/3。
      summaryFieldRows: [],
      summaryFieldLoading: false,
      summaryFieldError: '',
      vueCol: [],
      reqCol: [],
      v_inputrow: {},
      materialFieldLoading: false,
      materialFieldError: '',
      // 订单齐套汇总加载状态。
      suiteLoading: false,
      suiteLoaded: false,
      suiteError: '',
      suiteRequestId: 0,
      // 下游业务权限、字段草稿和写入恢复状态。
      permissions: { lock: false },
      // 请购字段筛选条件。
      materialSearch: '',
      appliedMaterialSearch: '',
      // 字段设置
      // 内置 ERP 仓存锁库弹窗
      erpLockDialogVisible: false,
      erpLockContexts: [],
      activeErpLockContext: null,
      erpLockStockRows: [],
      erpLockLockedRows: [],
      erpLockLoading: false,
      erpLockWriting: false,
      // 库位抽屉与真实数据
      locationDrawer: false,
      locationMode: 'single',
      locationTitle: '物料供给与位置明细',
      activeMaterialLocations: [],
      activeMaterialPoItems: [],
      supplyTraceRequestId: 0,
      locationOverviewRows: [],
      locationDetailDrawer: false,
      locationDetailTitle: '物料供给明细',
      activeDetailLocations: [],
      activeDetailPoItems: []
    };
  },
  computed: {
    summaryColumns() { return this.vueCol.filter(column => !['SEL', 'rownum', 'oper', 'MODIFY'].includes(column.key)); },
    summaryVeColumns() {
      const resetCol = {
        field: 'rownum',
        key: 'rownum',
        title: '序号',
        width: 46,
        align: 'center',
        fixed: 'left',
        disableResizing: true,
        renderHeaderCell: ({ column }) => {
          return (
            <div class="summary-reset-header" style="height: 100%; display: flex; align-items: center; justify-content: center;">
              <el-tooltip content="重置全部筛选" placement="top">
                <el-button
                  class="summary-filter-reset"
                  type="text"
                  icon="el-icon-delete-solid"
                  aria-label="重置全部筛选"
                  on-click={(e) => {
                    e.stopPropagation();
                    this.resetSummaryFilters();
                  }}
                />
              </el-tooltip>
            </div>
          );
        },
        renderBodyCell: ({ row, column, rowIndex }) => {
          return <span>{rowIndex + 1}</span>;
        }
      };

      const fieldCols = this.summaryColumns.map(column => {
        const colKey = column.key;
        const colTitle = column.title;
        const colEdcss = column.edcss;
        const isFixed = column.fixed || (colKey === 'IDNO' ? 'left' : false);

        return {
          field: colKey,
          key: colKey,
          title: colTitle,
          width: Number(column.width) || 120,
          align: column.align || 'left',
          fixed: isFixed,
          ellipsis: colKey === 'STFG' || colKey === 'OKPCT' || colEdcss === 'image' || colEdcss === 'checkbox' ? false : { showTitle: true },
          renderHeaderCell: ({ column: currentCol }) => {
            let filterNode = null;
            if (colKey === 'STFG') {
              filterNode = (
                <el-select
                  value={this.v_inputrow[colKey]}
                  class="summary-header-filter"
                  size="mini"
                  clearable
                  placeholder=""
                  on-change={(val) => {
                    this.$set(this.v_inputrow, colKey, val);
                    this.commitSummaryFilters();
                  }}
                  on-clear={() => {
                    this.$set(this.v_inputrow, colKey, '');
                    this.commitSummaryFilters();
                  }}
                >
                  {this.orderStatusOptions.map(item => (
                    <el-option key={item.value} label={item.label} value={item.value} />
                  ))}
                </el-select>
              );
            } else if (colEdcss === 'checkbox') {
              const options = colKey === 'ISFIN' ? this.finishOptions : this.checkOptions;
              filterNode = (
                <el-select
                  value={this.v_inputrow[colKey]}
                  class="summary-header-filter"
                  size="mini"
                  clearable
                  placeholder=""
                  on-change={(val) => {
                    this.$set(this.v_inputrow, colKey, val);
                    this.commitSummaryFilters();
                  }}
                  on-clear={() => {
                    this.$set(this.v_inputrow, colKey, '');
                    this.commitSummaryFilters();
                  }}
                >
                  {options.map(item => (
                    <el-option key={item.value} label={item.label} value={item.value} />
                  ))}
                </el-select>
              );
            } else if (colEdcss === 'date' || colEdcss === 'datetime' || colKey === 'ORDDTTM') {
              filterNode = (
                <el-date-picker
                  value={this.v_inputrow[colKey]}
                  class="summary-header-filter"
                  size="mini"
                  type="date"
                  clearable
                  value-format="yyyy-MM-dd"
                  placeholder=""
                  on-input={(val) => this.$set(this.v_inputrow, colKey, val)}
                  on-change={(val) => {
                    this.$set(this.v_inputrow, colKey, val);
                    this.commitSummaryFilters();
                  }}
                />
              );
            } else {
              filterNode = (
                <el-input
                  value={this.v_inputrow[colKey]}
                  class="summary-header-filter"
                  size="mini"
                  clearable
                  placeholder=""
                  type={colEdcss === 'number' ? 'number' : 'text'}
                  disabled={colEdcss === 'image'}
                  on-input={(val) => {
                    this.$set(this.v_inputrow, colKey, val);
                    this.handleSummaryFilterChange();
                  }}
                  on-clear={() => {
                    this.$set(this.v_inputrow, colKey, '');
                    this.commitSummaryFilters();
                  }}
                />
              );
            }

            return (
              <div class="summary-header-container" style="width:100%;box-sizing:border-box;">
                <div
                  class="summary-header-row summary-header-title"
                  title={colTitle}
                >
                  <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{colTitle}</span>
                </div>
                <div
                  class="summary-header-row summary-header-search"
                  on-mousemove={(e) => e.stopPropagation()}
                  on-click={(e) => e.stopPropagation()}
                >
                  {filterNode}
                </div>
              </div>
            );
          },
          renderBodyCell: ({ row, column: currentCol, rowIndex }) => {
            const val = row[colKey];
            if (colEdcss === 'image') {
              return (
                <el-image
                  fit="scale-down"
                  style="width:100%;height:30px;"
                  src={val}
                  preview-src-list={val ? [val] : []}
                >
                  <div slot="error"><i class="el-icon-picture-outline" /></div>
                </el-image>
              );
            } else if (colKey === 'STFG') {
              return (
                <el-tag
                  data-ui="badge"
                  size="mini"
                  effect="plain"
                  type={val === 'Y' ? 'success' : 'info'}
                >
                  {this.orderStatusLabel(val)}
                </el-tag>
              );
            } else if (colEdcss === 'checkbox') {
              return (
                <el-tag
                  data-ui="badge"
                  size="mini"
                  effect="plain"
                  type={val === 'Y' ? 'success' : 'info'}
                >
                  {this.checkStatusLabel(val, colKey)}
                </el-tag>
              );
            } else if (colKey === 'OKPCT') {
              const rate = this.getSummaryRate(val, row);
              if (rate == null) return this.displayValue(val);
              const isFilled = rate >= 40;
              const barColor = rate >= 100 ? '#10b981' : (rate > 0 ? '#3b82f6' : 'transparent');
              return (
                <div class="summary-progress-cell">
                  <div class="summary-progress-track">
                    <div
                      class="summary-progress-bar"
                      style={{
                        width: `${rate}%`,
                        backgroundColor: barColor
                      }}
                    />
                    <span
                      class="summary-progress-text"
                      style={{ color: isFilled ? '#ffffff' : '#374151' }}
                    >
                      {rate}%
                    </span>
                  </div>
                </div>
              );
            }
            return this.displayValue(val);
          }
        };
      });

      return [resetCol, ...fieldCols];
    },
    summaryRowStyleOption() {
      return {
        clickHighlight: true
      };
    },
    summaryCellStyleOption() {
      return {
        headerCellClass: () => 'summary-ve-header-cell',
        bodyCellClass: () => 'summary-ve-body-cell'
      };
    },
    summaryEventCustomOption() {
      return {
        bodyRowEvents: ({ row }) => ({
          click: () => {
            this.handleSummaryRowClick(row);
          }
        })
      };
    },
    materialEventCustomOption() {
      return null;
    },
    summaryColumnWidthResizeOption() {
      return {
        enable: true
      };
    },
    materialColumnWidthResizeOption() {
      return {
        enable: true
      };
    },
    isAllMaterialSelected() {
      const selectableRows = this.filteredNetMaterials.filter(row => this.materialSelectable(row));
      if (selectableRows.length === 0) return false;
      const set = new Set(this.selectedMaterialKeys);
      return selectableRows.every(row => set.has(row.rowKey));
    },
    isMaterialIndeterminate() {
      const selectableRows = this.filteredNetMaterials.filter(row => this.materialSelectable(row));
      if (selectableRows.length === 0) return false;
      const set = new Set(this.selectedMaterialKeys);
      const some = selectableRows.some(row => set.has(row.rowKey));
      const all = selectableRows.every(row => set.has(row.rowKey));
      return some && !all;
    },

    materialCellStyleOption() {
      return {
        headerCellClass: () => 'material-ve-header-cell',
        bodyCellClass: ({ row }) => {
          const statusClass = row ? this.postStatusClass(row.POSTFG) : '';
          return `material-ve-body-cell ${statusClass}`.trim();
        }
      };
    },
    requestColumns() { return this.reqCol.filter(column => !['SEL', 'rownum', 'oper'].includes(column.key)); },
    materialVeColumns() {
      const selectionCol = {
        field: 'SEL',
        key: 'SEL',
        title: '',
        width: 44,
        align: 'center',
        fixed: 'left',
        disableResizing: true,
        renderHeaderCell: () => {
          return (
            <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
              <el-checkbox
                value={this.isAllMaterialSelected}
                indeterminate={this.isMaterialIndeterminate}
                disabled={this.filteredNetMaterials.length === 0}
                on-change={this.toggleSelectAllMaterials}
              />
            </div>
          );
        },
        renderBodyCell: ({ row }) => {
          const selectable = this.materialSelectable(row);
          return (
            <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
              <el-checkbox
                value={this.isMaterialSelected(row)}
                disabled={!selectable}
                on-change={(val) => this.toggleSelectMaterial(row, val)}
              />
            </div>
          );
        }
      };

      const indexCol = {
        field: 'rownum',
        key: 'rownum',
        title: '序号',
        width: 48,
        align: 'center',
        fixed: 'left',
        disableResizing: true,
        renderBodyCell: ({ rowIndex }) => <span>{rowIndex + 1}</span>
      };

      const dataCols = this.requestColumns.map(column => {
        const colKey = column.key;
        const colTitle = column.title;
        const colEdcss = column.edcss;
        const colAlign = column.align || 'left';
        const colWidth = Number(column.width) || 120;
        const isFixed = column.fixed || false;

        return {
          field: colKey,
          key: colKey,
          title: colTitle,
          width: colWidth,
          align: colAlign,
          fixed: isFixed,
          ellipsis: colKey === 'MATNM' || colEdcss === 'image' || colEdcss === 'checkbox' ? false : { showTitle: true },
          renderHeaderCell: () => {
            return (
              <div
                class="material-ve-header-title"
                style="display: flex; align-items: center; width: 100%; height: 32px; line-height: 32px; padding: 0 4px; box-sizing: border-box;"
                title={colTitle}>
                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; font-weight: 600;">{colTitle}</span>
              </div>
            );
          },
          renderBodyCell: ({ row }) => {
            if (colEdcss === 'image') {
              return (
                <el-image
                  fit="scale-down"
                  style="width: 100%; height: 28px;"
                  src={row[colKey]}
                  preview-src-list={row[colKey] ? [row[colKey]] : []}
                />
              );
            }
            if (colKey === 'RMK' || colTitle === '备注') {
              if (!text(row[colKey])) return '';
              return <el-link type="primary" underline={false} on-click={(event) => {
                event.stopPropagation();
                this.openMaterialRemark(row);
              }}>{text(row[colKey])}</el-link>;
            }
            if (colKey === 'MATNM') {
              const val = this.displayValue(row[colKey]);
              return (
                <div class="material-name" title={val} style="display: flex; align-items: center; height: 100%; overflow: hidden;">
                  <span style="min-width:0;overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">{val}</span>
                </div>
              );
            }
            if (colEdcss === 'checkbox') {
              return <el-checkbox disabled value={row[colKey] === 'Y'} />;
            }
            return this.displayValue(row[colKey]);
          }
        };
      });

      const operCol = {
        field: 'oper',
        key: 'oper',
        title: '操作',
        width: 110,
        align: 'center',
        fixed: 'right',
        disableResizing: true,
        renderBodyCell: ({ row }) => {
          return (
            <div class="material-operation-actions" style="display: flex; align-items: center; justify-content: center; gap: 8px;">
              <button
                class="table-link"
                disabled={!this.permissions.lock}
                on-click={(e) => {
                  e.stopPropagation();
                  this.openSingleMaterialLock(row);
                }}>
                锁库
              </button>
              <button
                class="table-link"
                on-click={(e) => {
                  e.stopPropagation();
                  this.openSupplyTrace(row);
                }}>
                追踪
              </button>
            </div>
          );
        }
      };

      return [selectionCol, indexCol, ...dataCols, operCol];
    },
    toolbarSnapshot() {
      return {
        loading: this.suiteLoading || this.initialLoading,
        disabled: this.suiteLoading || this.initialLoading || this.erpLockWriting,
        snapshotAt: ''
      };
    },
    // 返回订单汇总表的标准数据源。
    orderSkuRows() {
      return this.orders;
    },
    // 暴露未经过筛选的订单汇总数据。
    rawSummary() {
      return this.orderSkuRows;
    },
    // vueCol 表头仅对元数据实际字段进行筛选。
    baseFilteredSummary() {
      return filterColumnRows(this.rawSummary, this.summaryFieldRows, this.appliedSummaryFilters);
    },
    filteredSummary() {
      return this.baseFilteredSummary;
    },
    selectedOrderLabel() {
      if (!this.selectedSummary) return '';
      const orderNo = this.selectedSummary.IDNO || '';
      const matNo = this.selectedSummary.MATNO || '';
      return matNo ? `${orderNo} / ${matNo}` : orderNo;
    },
    // 返回当前订单的请购明细。
    netMaterials() {
      return this.materialNetRows;
    },
    materialEmptyText() {
      if (this.initialLoading || this.suiteLoading || this.materialQueryLoading || this.materialFieldLoading ||
          this.suiteError || this.materialQueryError || this.materialFieldError || this.filteredNetMaterials.length) return '';
      if (!this.selectedSummary) return '请先选择订单';
      return this.netMaterials.length ? '没有符合筛选条件的请购明细' : '当前订单暂无请购明细';
    },
    searchedMaterials() {
      const query = text(this.appliedMaterialSearch).toLowerCase();
      if (!query) return this.netMaterials;
      const qty = numberOrNull(query);
      return this.netMaterials.filter(row =>
        text(row.MATNO || row.matNo).toLowerCase().includes(query)
        || text(row.MATNM || row.matName).toLowerCase().includes(query)
        || (qty != null && firstOptionalNumber(row, ['QTY', 'REQQTY']) === qty)
      );
    },
    // 请购明细按搜索结果和状态过滤，保持接口原始顺序。
    filteredNetMaterials() {
      let rows = this.searchedMaterials;
      if (this.materialStateFilter !== 'all') rows = rows.filter(row => text(row.POSTFG) === this.materialStateFilter);
      return rows;
    },
    materialStateCounts() {
      return this.searchedMaterials.reduce((counts, row) => {
        const state = text(row.POSTFG);
        counts[state] = (counts[state] || 0) + 1;
        return counts;
      }, Object.create(null));
    },
  },
  watch: {
    selectedSummary() {
      this.syncSummaryHighlight();
    },
    toolbarSnapshot: {
      immediate: true,
      handler(state) { if (this.toolbarInTabs) this.$emit('toolbar-change', state); }
    }
  },
  created() {
    this._disposed = false; this._active = true; this._suitePromise = null;
    this._summaryFilterTimer = null; this._resizeTimer = null; this._resizeFrame = null;
    this._materialSearchTimer = null;
    this._resizeCleanup = null; this._resizeObserver = null; this._lockRequestId = 0;
    this.appliedSummaryFilters = {};
    this.v_corpid = this.assemblyCorpid();
    this.v_username = this.$cookies && this.$cookies.get ? this.$cookies.get('v_telno') : '';
  },
  mounted() {
    this.updateLowerPaneHeight();
    Promise.all([this.getTabField(), this.loadMaterialFieldMetadata(), this.loadPermissions(), this.loadSuiteData()]
      .map(request => Promise.resolve(request).catch(error => {
        if (!this._disposed) this.$message.error(error.message || '初始化失败');
      }))).finally(() => { if (!this._disposed) this.initialLoading = false; });
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(this.handleWindowResize);
      this._resizeObserver.observe(this.$el);
    }
    window.addEventListener('resize', this.handleWindowResize);
  },
  activated() {
    this._active = true;
    window.addEventListener('resize', this.handleWindowResize);
    if (this._resizeObserver) this._resizeObserver.observe(this.$el);
    this.updateLowerPaneHeight();
    this.syncSummaryHighlight();
  },
  deactivated() {
    this._active = false; this.clearLayoutWork();
    this.commitMaterialSearch();
    window.removeEventListener('resize', this.handleWindowResize);
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this.closeSupplyTrace(); this.commitSummaryFilters();
  },
  beforeDestroy() {
    this._disposed = true;
    this.suiteRequestId++; this.materialQueryRequestId++; this.supplyTraceRequestId++; this._lockRequestId++;
    this.clearLayoutWork(); clearTimeout(this._summaryFilterTimer);
    clearTimeout(this._materialSearchTimer);
    window.removeEventListener('resize', this.handleWindowResize);
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },
  methods: {
    openMaterialRemark(row) {
      if (!text(row.IDSEQ) || !text(row.LSTSEQ)) {
        this.$message.warning('缺少请购单或请购行号，无法查询跟进信息');
        return;
      }
      this.materialRemarkRow = JSON.parse(JSON.stringify(row));
      this.materialRemarkVisible = true;
      this.loadMaterialRemarks();
    },
    async loadMaterialRemarks() {
      const requestId = ++this.materialRemarkRequestId;
      const row = this.materialRemarkRow;
      this.materialRemarkLoading = true;
      this.materialRemarkError = '';
      this.materialRemarkRows = [];
      this.materialRemarkFields = [];
      try {
        const [records, fields] = await Promise.all([
          getTabColOrData(this.v_url, 'v_wait_task', 'refidseq', row.IDSEQ, 'reflstseq', row.LSTSEQ, '', '', ' order by adddttm'),
          getTabColOrData(this.v_url, 'v_sys_form_fld', 'idseq', '101', 'lstseq', '4', '', '', ' order by sortby,itmseq')
        ]);
        if (this._disposed || requestId !== this.materialRemarkRequestId) return;
        this.materialRemarkRows = extractRows(records, '跟进记录读取失败');
        this.materialRemarkFields = extractRows(fields, '跟进字段读取失败').filter(field => field.THIDE === 'N');
      } catch (error) {
        if (!this._disposed && requestId === this.materialRemarkRequestId) this.materialRemarkError = error.message || '跟进记录读取失败';
      } finally {
        if (!this._disposed && requestId === this.materialRemarkRequestId) this.materialRemarkLoading = false;
      }
    },
    // 将程序选择同步到 VeTable；clickHighlight 只处理鼠标点击。
    syncSummaryHighlight() {
      this.$nextTick(() => {
        if (this._disposed || !this._active) return;
        const table = this.$refs.summaryTable;
        if (table) table.setHighlightRow({ rowKey: this.selectedSummary ? this.selectedSummary.rowKey : '' });
      });
    },
    orderStatusLabel(value) {
      const item = this.orderStatusOptions.find(item => item.value === value);
      return item ? item.label : this.displayValue(value);
    },
    checkStatusLabel(value, field) {
      const options = field === 'ISFIN' ? this.finishOptions : this.checkOptions;
      const item = options.find(item => item.value === value);
      return item ? item.label : this.displayValue(value);
    },
    // 从 Cookie 读取当前账套企业代码。
    assemblyCorpid() {
      return this.$cookies && this.$cookies.get ? this.$cookies.get('v_corpid') : '';
    },
    // 直连 getTabColOrData 核心查询方法
    // 查询当前账套的订单齐套汇总。
    getOrdList(corpid, cond) {
      const company = text(corpid || this.assemblyCorpid());
      if (!company) return Promise.resolve([]);
      return getTabColOrData(
        this.v_url || 'xuper_auth/getBaseData',
        'V_ORD_ONE',
        'corpid',
        company,
        '',
        '',
        '',
        '',
        cond || " and isfin='N' and  IDNO  like '%%'  and  ISFIN like '%N%' order by IDNO desc"
      ).then(res => {
        if (res && String(res.code) === '200' && Array.isArray(res.result)) {
          return res.result;
        }
        const msg = (res && (typeof res.result === 'string' ? res.result : res.message)) || '查询订单失败';
        throw new Error(msg);
      });
    },
    // 获取订单齐套汇总表列字段，表单配置为 idseq: 490050, lstseq: 2。
    getTabField() {
      let vm = this;
      if (this._disposed || this.summaryFieldLoading) return Promise.resolve();
      this.summaryFieldLoading = true;
      this.summaryFieldError = '';
      this.vueCol = [];
      this.summaryFieldRows = [];
      return getTabColOrData(this.v_url, 'v_sys_form_fld', 'idseq', '490050', 'lstseq', '2', '', '', ' order by sortby,lstseq').then((res) => {
        const fields = extractRows(res, '订单字段读取失败');
        return getTabColOrData(vm.v_url, 'V_FORM_FLDSET', 'idseq', '490050', 'userno', vm.v_username, 'corpid', vm.v_corpid, ' order by sortby,lstseq')
          .catch(() => ({ code: 200, result: [] }))
          .then((fldSetRes) => {
            const scope = {
              settingFormId: '490050',
              fieldIdseq: '490050',
              fieldLstseq: '2',
              userNo: vm.v_username,
              corpId: vm.v_corpid
            };
            const customRows = (fldSetRes && Array.isArray(fldSetRes.result)) ? fldSetRes.result : [];
            const settings = mergeFieldSettings(fields, customRows, scope, { lockedFieldKeys: ['SMPNO'] });
            if (settings.conflicts.length) throw new Error('订单字段配置存在重复记录');
            const configMap = new Map(settings.rows.map(item => [item.CODE + '::' + item.REFITMSEQ, item]));
            const mainFields = fields.filter(item => (item.THIDE || item.thide) === 'N').map(item => {
              const fieldKey = item.key || item.KEY || item.fldname || item.FLDNAME || item.code || item.CODE;
              const itmSeq = item.ITMSEQ != null ? item.ITMSEQ : item.itmseq;
              const saved = configMap.get(fieldKey + '::' + itmSeq);
              const resolvedWidth = saved && (saved.WIDTH != null ? saved.WIDTH : saved.width) != null
                ? (saved.WIDTH != null ? saved.WIDTH : saved.width)
                : (item.width != null ? item.width : (item.WIDTH != null ? item.WIDTH : item.COLWIDTH));
              return Object.assign({}, item, {
                key: fieldKey,
                width: resolvedWidth,
                SORTBY: saved ? saved.SORTBY : item.SORTBY,
                VISFLAG: saved ? saved.VISFLAG : item.VISFLAG
              });
            }).sort((a, b) => Number(a.SORTBY || 0) - Number(b.SORTBY || 0));
            if (vm._disposed) return;
            vm.summaryFieldRows = mainFields.filter(item => item.VISFLAG !== 'N');
            for (let k = 0; k < vm.summaryFieldRows.length; k++) {
              let itcol = vm.summaryFieldRows[k];
              const rawWidth = itcol.width != null ? itcol.width : (itcol.WIDTH != null ? itcol.WIDTH : itcol.COLWIDTH);
              const parsedWidth = Number(rawWidth);
              const colWidth = (!isNaN(parsedWidth) && parsedWidth > 0) ? parsedWidth : 120;
              const fieldKey = itcol.key || itcol.KEY || itcol.fldname || itcol.FLDNAME;
              const fieldTitle = (vm.$translate && vm.$translate.getColTitle)
                ? vm.$translate.getColTitle(itcol, vm.$store.state.lang)
                : (itcol.title || itcol.TITLE || itcol.name || itcol.NAME || itcol.CHNDESC || fieldKey);
              const fieldAlign = itcol.align || itcol.ALIGN || (itcol.CALIGN ? String(itcol.CALIGN).toLowerCase() : '') || 'left';
              vm.vueCol.push({
                field: fieldKey,
                title: fieldTitle,
                key: fieldKey,
                align: fieldAlign,
                width: colWidth,
                thide: itcol.THIDE || itcol.thide,
                fhide: itcol.FHIDE || itcol.fhide,
                edcss: itcol.EDCSS || itcol.edcss,
                disabled: itcol.DISABLED || itcol.disabled,
                fixed: itcol.fixed || (fieldKey === 'IDNO' ? 'left' : ''),
                sortable: false
              });
              if (vm.v_inputrow[fieldKey] === undefined) vm.$set(vm.v_inputrow, fieldKey, '');
            }
          });
      }).catch((error) => {
        if (!vm._disposed) vm.summaryFieldError = error.message || '订单字段读取失败';
      }).finally(() => {
        if (!vm._disposed) vm.summaryFieldLoading = false;
      });
    },
    // 获取请购明细列字段，使用系统表单配置 490050/3（不使用个性化设置）。
    loadMaterialFieldMetadata() {
      let vm = this;
      if (this._disposed || this.materialFieldLoading) return Promise.resolve();
      this.materialFieldLoading = true;
      this.materialFieldError = '';
      this.reqCol = [];
      return getTabColOrData(this.v_url, 'v_sys_form_fld', 'idseq', '490050', 'lstseq', '3', '', '', ' order by sortby,lstseq').then((res) => {
        if (vm._disposed) return;
        const fields = extractRows(res, '请购汇总字段读取失败').filter(item => item.THIDE === 'N');
        for (let k = 0; k < fields.length; k++) {
          let itcol = fields[k];
          const rawWidth = itcol.width != null ? itcol.width : (itcol.WIDTH != null ? itcol.WIDTH : itcol.COLWIDTH);
          const parsedWidth = Number(rawWidth);
          const colWidth = (!isNaN(parsedWidth) && parsedWidth > 0) ? parsedWidth : 120;
          vm.reqCol.push({
            field: itcol.key,
            title: vm.$translate.getColTitle(itcol, vm.$store.state.lang),
            key: itcol.key,
            align: itcol.align,
            width: colWidth,
            minWidth: 80,
            thide: itcol.THIDE,
            fhide: itcol.FHIDE,
            edcss: itcol.EDCSS,
            disabled: itcol.DISABLED,
            fixed: itcol.key === 'SEL' ? 'left' : (itcol.ISFIX === 'Y' ? 'left' : itcol.ISFIX === 'R' ? 'right' : (itcol.fixed || '')),
            sortable: false
          });
        }
      }).catch((error) => {
        if (!vm._disposed) vm.materialFieldError = error.message || '请购汇总字段读取失败';
      }).finally(() => {
        if (!vm._disposed) vm.materialFieldLoading = false;
      });
    },
    // 查询锁库权限。
    loadPermissions() {
      const forms = [{ key: 'lock', id: '16572' }];
      return Promise.all(forms.map(form => getTabColOrData(
        this.v_url || 'xuper_auth/getBaseData',
        'v_sys_user_priv',
        'userno',
        this.v_username,
        'frmidseq',
        form.id,
        '',
        '',
        " and issel='Y'"
      ).then(res => ({ key: form.key, allowed: String(res && res.code) === '200' && Array.isArray(res.result) && res.result.length > 0 })).catch(() => ({ key: form.key, allowed: false })))).then(results => {
        if (!this._disposed) results.forEach(item => { this.permissions[item.key] = item.allowed; });
      });
    },
    // V_ORD_ONE.IDSEQ 直接关联 V_po_reqdtl.ORDSEQ。
    getReqData(orderIdseq) {
      return getTabColOrData(this.v_url, 'V_po_reqdtl', 'ordseq', orderIdseq,
        'corpid', this.assemblyCorpid(), '', '', ' order by idno, matno')
        .then(res => extractRows(res, '请购明细读取失败'));
    },
    loadSuiteData(force) {
      if (this._disposed || this.erpLockWriting) return Promise.resolve(false);
      if (this.suiteLoading) return this._suitePromise;
      if (this.suiteLoaded && !force) return Promise.resolve(true);
      const corpid = this.assemblyCorpid();
      if (!corpid) {
        this.clearMaterialContext(); this.resetNetMaterialFilters(); this.orders = EMPTY_ROWS; this.selectedSummary = null;
        this.suiteError = '缺少账套企业代码 (v_corpid)'; return Promise.resolve(false);
      }
      const previous = this.selectedSummary && (this.selectedSummary.rowKey || this.selectedSummary.IDSEQ);
      const requestId = ++this.suiteRequestId;
      this.clearMaterialContext();
      this.suiteLoading = true; this.suiteError = '';
      const promise = this.getOrdList(corpid).then(async raw => {
        if (this._disposed || requestId !== this.suiteRequestId) return false;
        const rows = filterSuiteRows(raw, corpid), keys = new Set();
        rows.forEach((row, idx) => {
          let rk = row.rowKey || [row.IDSEQ, row.LSTSEQ].filter(Boolean).join('::') || `ORD_ROW_${idx}`;
          if (keys.has(rk)) {
            rk = `${rk}::${idx}`;
            row.rowKey = rk;
          }
          keys.add(rk);
        });
        this.orders = freezeSnapshot(rows);
        const nextRow = rows.find(row => row.rowKey === previous || row.IDSEQ === previous) || rows[0] || null;
        this.suiteLoaded = true;
        if (nextRow) await this.applySummaryRow(nextRow);
        else {
          this.selectedSummary = null;
          this.resetNetMaterialFilters();
        }
        return true;
      }).catch(error => {
        if (this._disposed || requestId !== this.suiteRequestId) return false;
        this.orders = EMPTY_ROWS; this.selectedSummary = null;
        this.resetNetMaterialFilters();
        this.suiteError = error.message || '订单读取失败';
        return false;
      }).finally(() => {
        if (!this._disposed && requestId === this.suiteRequestId) { this.suiteLoading = false; this._suitePromise = null; }
      });
      this._suitePromise = promise;
      return promise;
    },
    async syncSuiteOrders() {
      if (this._disposed) return false;
      const corpid = this.assemblyCorpid();
      if (!corpid) return false;
      const previous = this.selectedSummary && (this.selectedSummary.rowKey || this.selectedSummary.IDSEQ);
      try {
        const raw = await this.getOrdList(corpid);
        if (this._disposed) return false;
        const rows = filterSuiteRows(raw, corpid), keys = new Set();
        rows.forEach((row, idx) => {
          let rk = row.rowKey || [row.IDSEQ, row.LSTSEQ].filter(Boolean).join('::') || `ORD_ROW_${idx}`;
          if (keys.has(rk)) {
            rk = `${rk}::${idx}`;
            row.rowKey = rk;
          }
          keys.add(rk);
        });
        this.orders = freezeSnapshot(rows);
        if (previous) {
          const nextRow = rows.find(row => row.rowKey === previous || row.IDSEQ === previous);
          if (nextRow) this.selectedSummary = nextRow;
          else {
            this.selectedSummary = null;
            this.clearMaterialContext();
            this.resetNetMaterialFilters();
          }
        }
        return true;
      } catch (error) {
        return false;
      }
    },
    async refreshSelectedMaterials() {
      if (this._disposed) return false;
      const row = this.selectedSummary, requestId = ++this.materialQueryRequestId;
      const selectedKeys = this.selectedMaterialKeys.slice();
      this.materialQueryError = ''; this.materialNetRows = EMPTY_ROWS; this.clearSelectedMaterials();
      if (!row) { this.materialQueryLoading = false; return false; }
      this.materialQueryLoading = true;
      try {
        const rows = await this.getReqData(row.IDSEQ);
        if (this._disposed || requestId !== this.materialQueryRequestId || this.selectedSummary !== row) return false;
        this.materialNetRows = freezeSnapshot(normalizeRequestRows(rows));
        this.syncSelectedMaterialsFromKeys(selectedKeys);
        return true;
      } catch (error) {
        if (!this._disposed && requestId === this.materialQueryRequestId) this.materialQueryError = error.message || '请购明细读取失败';
        return false;
      } finally {
        if (!this._disposed && requestId === this.materialQueryRequestId) this.materialQueryLoading = false;
      }
    },
    materialRowClassName({ row }) { return this.postStatusClass(row.POSTFG); },
    // 返回订单表稳定的复合行键。
    summaryRowKey(row) {
      if (!row) return '';
      return row.orderKey || row.rowKey || '';
    },
    // 控制净物料行是否允许选择。
    materialSelectable(row) {
      return !!row && !!text(row.MATNO || row.matNo);
    },
    setReadinessFilter() {},
    handleSummaryFilterChange() {
      clearTimeout(this._summaryFilterTimer);
      this._summaryFilterTimer = setTimeout(() => this.commitSummaryFilters(), 200);
    },
    commitSummaryFilters() {
      clearTimeout(this._summaryFilterTimer);
      if (this._disposed) return;
      this.appliedSummaryFilters = Object.assign({}, this.v_inputrow);
    },
    // 清空订单表全部筛选条件。
    resetSummaryFilters() {
      this.v_inputrow = {};
      this.commitSummaryFilters();
    },
    // 点击订单行时将其设为当前分析对象。
    handleSummaryRowClick(row) {
      return this.selectSummaryRow(row);
    },
    // 一次仅查看一个订单；请求中的物料不能沿用上个订单。
    selectSummaryRow(row) {
      if (!row || this._disposed || this.erpLockWriting || this.suiteLoading) {
        this.syncSummaryHighlight();
        return Promise.resolve(false);
      }
      return this.applySummaryRow(row);
    },
    applySummaryRow(row) {
      const changed = !this.selectedSummary || this.summaryRowKey(this.selectedSummary) !== this.summaryRowKey(row);
      if (changed) this.resetNetMaterialFilters();
      this.clearMaterialContext();
      this.selectedSummary = row;
      return this.refreshSelectedMaterials();
    },
    clearMaterialContext() {
      this.materialQueryRequestId++;
      this.materialQueryLoading = false;
      this.materialQueryError = '';
      this.materialNetRows = EMPTY_ROWS;
      this.clearSelectedMaterials();
      this.closeSupplyTrace();
      this.erpLockDialogVisible = false;
      this._lockRequestId++;
      this.erpLockLoading = false;
      this.erpLockStockRows = []; this.erpLockLockedRows = [];
      this.erpLockContexts = []; this.activeErpLockContext = null;
    },
    // 更新物料状态筛选条件。
    setMaterialStateFilter(filter) {
      this.materialStateFilter = filter;
    },
    toggleSelectAllMaterials(val) {
      const selectableRows = this.filteredNetMaterials.filter(row => this.materialSelectable(row));
      const set = new Set(this.selectedMaterialKeys);
      if (val) {
        selectableRows.forEach(row => set.add(row.rowKey));
      } else {
        selectableRows.forEach(row => set.delete(row.rowKey));
      }
      this.selectedMaterials = this.netMaterials.filter(row => set.has(row.rowKey));
      this.selectedMaterialKeys = this.selectedMaterials.map(row => row.rowKey);
    },
    toggleSelectMaterial(row, val) {
      if (!row) return;
      const set = new Set(this.selectedMaterialKeys);
      if (val) {
        set.add(row.rowKey);
      } else {
        set.delete(row.rowKey);
      }
      this.selectedMaterials = this.netMaterials.filter(r => set.has(r.rowKey));
      this.selectedMaterialKeys = this.selectedMaterials.map(r => r.rowKey);
    },
    isMaterialSelected(row) {
      return !!row && this.selectedMaterialKeys.includes(row.rowKey);
    },
    lockContextRowKey(row) {
      if (!row) return '';
      return row.lockContextKey || [row.corpid, row.reqSeq, row.reqItm, row.matNo || row.MATNO, row.unit || row.UNITNM].join('::');
    },
    locationOverviewRowKey(row) {
      if (!row) return '';
      return row.rowKey;
    },
    // 仅清空净物料表筛选条件。
    resetNetMaterialFilters() {
      this.materialStateFilter = 'all';
      this.materialSearch = '';
      this.commitMaterialSearch();
    },
    handleMaterialSearch() {
      clearTimeout(this._materialSearchTimer);
      if (this._disposed) return;
      this._materialSearchTimer = setTimeout(() => this.commitMaterialSearch(), 200);
    },
    commitMaterialSearch() {
      clearTimeout(this._materialSearchTimer);
      this._materialSearchTimer = null;
      if (this._disposed) return;
      this.appliedMaterialSearch = this.materialSearch;
    },
    clearSelectedMaterials() {
      this.selectedMaterials = [];
      this.selectedMaterialKeys = [];
    },
    syncSelectedMaterialsFromKeys(keys) {
      const selected = new Set(keys);
      this.selectedMaterials = this.netMaterials.filter(row => selected.has(row.rowKey));
      this.selectedMaterialKeys = this.selectedMaterials.map(row => row.rowKey);
    },
    // 拖拽按容器高度夹取，鼠标释放、失焦、停用和销毁均清理监听。
    startResize(event) {
      if (event.button !== 0 || this._disposed || !this._active) return;
      event.preventDefault();
      if (this._resizeCleanup) this._resizeCleanup();
      const startY = event.clientY, startHeight = this.upperHeight;
      let latestY = startY;
      const update = () => {
        this._resizeFrame = null;
        const height = this.availablePaneHeight(), min = Math.min(160, height / 2);
        this.upperHeight = Math.max(min, Math.min(height - min, startHeight + latestY - startY));
        this.updateLowerPaneHeight();
      };
      const move = e => { latestY = e.clientY; if (this._resizeFrame == null) this._resizeFrame = requestAnimationFrame(update); };
      const stop = () => {
        document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', stop); window.removeEventListener('blur', stop);
        if (this._resizeFrame != null) { cancelAnimationFrame(this._resizeFrame); if (!this._disposed && this._active) update(); }
        this._resizeFrame = null; this._resizeCleanup = null;
      };
      this._resizeCleanup = stop;
      document.addEventListener('mousemove', move); document.addEventListener('mouseup', stop); window.addEventListener('blur', stop);
    },
    clearLayoutWork() {
      clearTimeout(this._resizeTimer);
      if (this._resizeCleanup) this._resizeCleanup();
      if (this._resizeFrame != null) cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = null;
    },
    updateLowerPaneHeight() {
      this.$nextTick(() => {
        if (this._disposed || !this._active) return;
        const height = this.availablePaneHeight(), min = Math.min(160, height / 2);
        const next = Math.max(min, Math.min(height - min, this.upperHeight));
        if (next !== this.upperHeight) this.upperHeight = next;
      });
    },
    handleWindowResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.updateLowerPaneHeight(), 100);
    },
    availablePaneHeight() {
      const children = Array.from(this.$el.children);
      const reserved = children.filter(el => el.matches('.query-band, .splitter, .material-query-error')).reduce((sum, el) => sum + el.offsetHeight, 0);
      return Math.max(0, this.$el.clientHeight - reserved);
    },
    getSummaryRate(val, row) {
      if (val !== undefined && val !== null && val !== '' && val !== '—') {
        const num = parseFloat(String(val).replace('%', ''));
        if (!isNaN(num)) return Math.min(100, Math.max(0, num));
      }
      const total = Number((row && (row.MATCNT != null ? row.MATCNT : row.ROWCNT)) || 0);
      const ok = Number((row && (row.OKCNT != null ? row.OKCNT : row.OKMAT)) || 0);
      if (total > 0) {
        return Math.min(100, Math.max(0, Math.round((ok / total) * 100)));
      }
      return null;
    },
    displayValue(value) { return value === '' || value == null ? '' : String(value); },
    formatSummaryCell(row, column, value) { return this.displayValue(value); },
    postStatusClass(status) { return POST_STATUS[text(status)] || 'status-unknown'; },
    // 单条请购分录锁库，订单主键不能替代请购 IDSEQ/LSTSEQ。
    async openSingleMaterialLock(row) {
      if (!row || !this.permissions.lock || this.erpLockWriting || this._disposed) return;
      const corpid = this.assemblyCorpid(), reqSeq = text(row.IDSEQ), reqItm = text(row.LSTSEQ), matNo = text(row.MATNO);
      if (!corpid || !reqSeq || !reqItm || !matNo) { this.$message.warning('缺少请购明细身份，不能锁库'); return; }
      const demandQty = firstOptionalNumber(row, ['REQQTY', 'QTY']);
      const context = Object.assign({}, row, {
        lockContextKey: [corpid, reqSeq, reqItm, matNo, unitOf(row)].join('::'),
        corpid, reqSeq, reqItm, matNo, unit: unitOf(row), matName: text(row.MATNM),
        demandQty, availableQty: null, lockedQty: firstOptionalNumber(row, ['LOCKQTY']),
        remainingQty: demandQty == null ? null : Math.max(0, demandQty - number(row.LOCKQTY, 0))
      });
      this.activeErpLockContext = context;
      this.erpLockContexts = [context];
      this.erpLockDialogVisible = true;
      return this.refreshErpLockData();
    },
    handleErpLockContextClick(row) {
      if (this.erpLockWriting) return;
      this.activeErpLockContext = row;
      return this.refreshErpLockData();
    },
    refreshActiveErpLockStock() { if (!this.erpLockWriting) return this.refreshErpLockData(); },
    async refreshErpLockData() {
      if (this._disposed || !this.erpLockDialogVisible || !this.activeErpLockContext) return false;
      const context = this.activeErpLockContext, requestId = ++this._lockRequestId;
      this.erpLockLoading = true;
      this.erpLockStockRows = []; this.erpLockLockedRows = [];
      try {
        const results = await Promise.all([
          getTabColOrData(this.v_url, 'V_IC_STKLOG', 'corpid', context.corpid, 'matno', context.matNo, '', '', ' order by STKNM desc'),
          getTabColOrData(this.v_url, 'V_IC_STKLOG_DTL', 'corpid', context.corpid, 'reqseq', context.reqSeq, 'reqitm', context.reqItm, ' order by idseq desc')
        ]);
        if (this._disposed || requestId !== this._lockRequestId || this.activeErpLockContext !== context || !this.erpLockDialogVisible) return false;
        this.erpLockStockRows = extractRows(results[0], '读取库存失败').map(row => Object.assign({}, row, { ALLOQTY: null }));
        this.erpLockLockedRows = extractRows(results[1], '读取锁库记录失败').filter(row => text(row.MATNO) === context.matNo);
        const lockedQty = sumKnown(this.erpLockLockedRows, 'QTY');
        const availableQty = sumKnown(this.erpLockStockRows, 'OKQTY');
        const updated = Object.assign({}, context, {
          availableQty,
          lockedQty,
          remainingQty: context.demandQty == null || lockedQty == null ? null : Math.max(0, context.demandQty - lockedQty)
        });
        this.activeErpLockContext = updated; this.erpLockContexts = [updated];
        return true;
      } catch (error) {
        if (!this._disposed && requestId === this._lockRequestId) this.$message.error(error.message || '锁库数据读取失败');
        return false;
      } finally {
        if (!this._disposed && requestId === this._lockRequestId) this.erpLockLoading = false;
      }
    },
    async writeLock(action, row) {
      if (this._disposed || this.erpLockWriting || this.erpLockLoading || !this.permissions.lock) return;
      const context = this.activeErpLockContext;
      if (!context || !row) return;
      const qty = numberOrNull(row.ALLOQTY);
      if (action === 'allocate') {
        const available = firstOptionalNumber(row, ['OKQTY', 'USEQTY']);
        if (qty == null || qty <= 0 || available == null || context.remainingQty == null ||
            qty > available || qty > context.remainingQty) {
          this.$message.warning('分配数量不能超过当前可用量和剩余需求'); return;
        }
        if (context.unit && unitOf(row) && context.unit !== unitOf(row)) { this.$message.warning('库存单位与请购单位不一致'); return; }
      }
      if (!text(row.IDSEQ) || !text(row.LSTSEQ)) { this.$message.warning('库存记录身份不完整'); return; }
      this.erpLockWriting = true;
      let committed = false;
      try {
        try {
          await this.$confirm(action === 'allocate' ? '确定分配该仓位库存吗？' : '确定释放该条锁库记录吗？', '提示', { type: 'warning' });
        } catch (cancel) { return; }
        if (this._disposed) return;
        const data = action === 'allocate' ? {
          p_json: JSON.stringify({ IDSEQ: row.IDSEQ, LSTSEQ: row.LSTSEQ, STKNO: row.STKNO, LOTNO: row.LOTNO,
            REQSEQ: context.reqSeq, REQITM: context.reqItm, CORPID: context.corpid, MATNO: context.matNo, QTY: qty }),
          p_table: 'IC_STKLOG_DTL', p_idseq: row.IDSEQ
        } : {
          p_key: 'idseq', p_value: row.IDSEQ, p_key2: 'lstseq', p_value2: row.LSTSEQ,
          p_key3: 'corpid', p_value3: context.corpid, p_table: 'IC_STKLOG_DTL'
        };
        const res = await this.$axios({ method: 'post', url: 'xuper_auth/' + (action === 'allocate' ? 'tableBodySave' : 'delTabRec'), data });
        if (!res || String(res.code) !== '200') throw new Error(text(res && (res.message || res.result)) || '锁库操作失败');
        committed = true;
        if (this._disposed) return;
        this.$message.success(action === 'allocate' ? '锁库分配成功' : '已释放锁库记录');
        const results = await Promise.all([
          this.refreshSelectedMaterials(),
          this.refreshErpLockData(),
          this.syncSuiteOrders()
        ]);
        if (results.some(result => !result)) this.$message.warning('锁库操作已成功，部分数据回读失败，请刷新');
        if (this.locationDrawer) await this.reloadSupplyTrace();
      } catch (error) {
        if (!this._disposed) this.$message.error(committed ? '锁库操作已成功，回读失败，请刷新' : error.message || '锁库操作失败');
      } finally { if (!this._disposed) this.erpLockWriting = false; }
    },
    allocateErpLock(row) { return this.writeLock('allocate', row); },
    releaseErpLock(row) { return this.writeLock('release', row); },
    closeLockDialog() { this._lockRequestId++; this.erpLockLoading = false; },

    // 请购信息复用选中明细；打开或刷新追踪时仅重新查询库存。
    openSupplyTrace(row) { return this.startSupplyTrace(row ? [row] : [], 'single'); },
    openSelectedLocation() { return this.startSupplyTrace(this.selectedMaterials, 'multiple'); },
    startSupplyTrace(rows, mode) {
      if (!this.selectedSummary || this._disposed) return;
      const targets = new Map();
      (rows || []).forEach(row => {
        const matNo = text(row.MATNO || row.matNo);
        if (!matNo) return;
        if (!targets.has(matNo)) targets.set(matNo, { matNo, matName: text(row.MATNM || row.matName), units: [], rowKeys: [] });
        const target = targets.get(matNo), units = target.units;
        if (!units.includes(unitOf(row))) units.push(unitOf(row));
        if (!target.rowKeys.includes(row.rowKey)) target.rowKeys.push(row.rowKey);
      });
      if (!targets.size) { this.$message.warning('请选择有物料编码的记录'); return; }
      this.traceMaterialTargets = Array.from(targets.values());
      this.locationMode = mode;
      this.locationTitle = mode === 'single' ? this.traceMaterialTargets[0].matNo + ' · 供给追踪' : '所选物料供给追踪总览';
      this.locationDrawer = true;
      this.locationDetailDrawer = false;
      return this.reloadSupplyTrace();
    },
    async reloadSupplyTrace() {
      if (this._disposed || !this.locationDrawer || !this.selectedSummary) return;
      const requestId = ++this.supplyTraceRequestId, idseq = this.selectedSummary.IDSEQ, corpid = this.assemblyCorpid();
      this.traceLoading = true; this.traceError = '';
      this.locationOverviewRows = []; this.activeMaterialLocations = []; this.activeMaterialPoItems = [];
      this.activeDetailLocations = []; this.activeDetailPoItems = [];
      this.activeLocationStatuses = []; this.activeDetailStatuses = [];
      try {
        if (this.materialQueryLoading) throw new Error('请购明细正在加载，请稍后刷新库存');
        if (this.materialQueryError) throw new Error(this.materialQueryError);
        // 按打开时的行键关联当前明细，锁库回读后沿用新值，不混入未勾选的同物料记录。
        const requestRowsByKey = new Map(this.materialNetRows.map(row => [row.rowKey, row]));
        const targets = this.traceMaterialTargets.map(target => Object.assign({}, target, {
          requestRows: target.rowKeys.map(key => requestRowsByKey.get(key))
            .filter(row => row && text(row.MATNO || row.matNo) === target.matNo)
        })).filter(target => target.requestRows.length);
        const results = await mapWithLimit(targets, async target => {
          if (requestId !== this.supplyTraceRequestId || this._disposed) return { groups: [], error: '' };
          const stocks = await getTabColOrData(this.v_url, 'V_IC_STKLOG', 'corpid', corpid, 'matno', target.matNo, '', '', ' order by STKNM desc')
            .then(res => ({ rows: extractRows(res, '库存查询失败'), error: '' })).catch(error => ({ rows: [], error: error.message || '库存查询失败' }));
          return { groups: buildSupplyGroups(target, stocks, target.requestRows), error: stocks.error ? target.matNo + '：' + stocks.error : '' };
        });
        if (this._disposed || requestId !== this.supplyTraceRequestId || !this.locationDrawer || !this.selectedSummary || this.selectedSummary.IDSEQ !== idseq) return;
        this.locationOverviewRows = freezeSnapshot(results.reduce((all, result) => all.concat(result.groups), []));
        this.traceError = results.map(result => result.error).filter(Boolean).join('；');
        this.activeMaterialLocations = this.locationOverviewRows.reduce((all, row) => all.concat(row.locations), []);
        this.activeMaterialPoItems = this.locationOverviewRows.reduce((all, row) => all.concat(row.poItems), []);
        this.activeLocationStatuses = Array.from(new Set(this.locationOverviewRows.reduce((all, row) => all.concat(row.statuses), [])));
        if (this.locationDetailDrawer) {
          const detail = this.locationOverviewRows.find(row => row.rowKey === this.activeDetailKey);
          if (detail) this.openLocationDetail(detail); else this.locationDetailDrawer = false;
        }
      } catch (error) {
        if (!this._disposed && requestId === this.supplyTraceRequestId) this.traceError = error.message || '供给查询失败';
      } finally {
        if (!this._disposed && requestId === this.supplyTraceRequestId) this.traceLoading = false;
      }
    },
    invalidateSupplyTrace() {
      this.supplyTraceRequestId++;
      this.traceLoading = false;
      this.locationDetailDrawer = false;
    },
    closeSupplyTrace() {
      this.invalidateSupplyTrace();
      this.locationDrawer = false;
      this.locationOverviewRows = [];
      this.traceMaterialTargets = [];
      this.activeMaterialLocations = []; this.activeMaterialPoItems = [];
      this.activeDetailLocations = []; this.activeDetailPoItems = [];
      this.traceError = '';
    },
    openLocationDetail(row) {
      this.activeDetailKey = row.rowKey;
      this.locationDetailTitle = row.matNo + ' · ' + (row.unit || '单位未提供') + ' · 供给详情';
      this.activeDetailLocations = row.locations;
      this.activeDetailPoItems = row.poItems;
      this.activeDetailStatuses = row.statuses;
      this.locationDetailDrawer = true;
    }
  }
};
</script>



<style scoped>
.kit-material-workbench {
  --background: #f6f7f9;
  --foreground: #0b1019;
  --card: #ffffff;
  --card-foreground: #0b1019;
  --popover: #ffffff;
  --popover-foreground: #0b1019;
  --primary: #2563eb;
  --primary-foreground: #ffffff;
  --secondary: #f4f4f5;
  --secondary-foreground: #0b1019;
  --muted: #f4f4f5;
  --muted-foreground: #a6abb4;
  --accent: #eff6ff;
  --accent-foreground: #1d4ed8;
  --destructive: #dc2626;
  --destructive-foreground: #ffffff;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --ring: #2563eb;
  --success: #15803d;
  --success-soft: #ecfdf3;
  --warning: #b45309;
  --warning-soft: #fff7ed;
  --shortage-row: #fff1f2;
  --shortage-row-hover: #ffe4e6;
  --radius: 6px;
  --shadow-overlay: 0 18px 48px rgba(24, 24, 27, 0.16);
  --bg: var(--background);
  --surface: var(--card);
  --surface-subtle: #fafafa;
  --line: var(--border);
  --line-strong: var(--input);
  --text: var(--foreground);
  --primary-soft: var(--accent);
  --danger: var(--destructive);
  --danger-soft: var(--shortage-row);
  --font-size-body: 12px;
  --font-size-hint: 12px;
  --font-size-table-header: 12px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--bg);
  color: var(--text);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: var(--font-size-body);
  box-sizing: border-box;
}

.query-band {
  flex: 0 0 auto;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0;
  overflow: hidden;
}

.dimension-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 36px;
  min-height: 36px;
  padding: 0 16px;
  background: var(--surface);
}


.dimension-nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--surface);
  border: 1px solid var(--line);
  border-top: 0;
}
.upper-pane { flex: 0 0 auto; }
.lower-pane { flex: 1 1 auto; border-top: 1px solid var(--line); }
.table-wrap { flex: 1; min-height: 0; overflow: hidden; }
.material-query-error {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  padding: 0 10px;
  color: var(--danger);
  background: #fff5f5;
  border-top: 1px solid #fecaca;
  border-bottom: 1px solid #fecaca;
  font-size: 12px;
}
.material-query-error span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.splitter {
  position: relative;
  flex: 0 0 9px;
  cursor: row-resize;
  background: var(--muted);
  border-left: 1px solid var(--line);
  border-right: 1px solid var(--line);
}
.splitter::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 50%;
  width: 42px;
  height: 3px;
  border-radius: 3px;
  background: var(--input);
  transform: translateX(-50%);
}
.splitter:hover::after { background: var(--primary); }

.material-workbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.selected-sku-context {
  flex: 1 1 90px;
  min-width: 0;
  color: var(--muted-foreground);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.material-state-filter {
  display: flex;
  align-items: center;
  gap: 4px;
}
.state-filter-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 22px;
  padding: 0 10px !important;
  border-radius: 4px;
  border: 1px solid #cbd5e1 !important;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s;
}
.state-filter-button:hover { background: #f1f5f9; }
.state-filter-button.is-active { background: #0f172a !important; color: #fff !important; border-color: #0f172a !important; }
.state-filter-button.ord-suite-status-pending {
  padding: 0 10px !important;
  border: 1px solid #cbd5e1 !important;
  color: #909399 !important;
}

.selection-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shadcn-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius);
  font-weight: 500;
  font-size: 12px;
  height: 28px;
  padding: 0 10px;
  transition: all 0.15s ease;
}
.shadcn-button--outline { background: #fff; color: #0f172a; border: 1px solid #cbd5e1; }
.shadcn-button--outline:hover { background: #f1f5f9; }
.shadcn-button--ghost { background: transparent; color: #0f172a; border: none; }
.shadcn-button--ghost:hover { background: #f1f5f9; }

.danger-text { color: #dc2626; font-weight: 600; }
.success-text { color: #16a34a; font-weight: 600; }



.table-link {
  background: none;
  border: none;
  color: #2563eb !important;
  cursor: pointer;
  font-size: 12px;
  padding: 0 4px;
}
.table-link:hover { text-decoration: underline; }
.table-link:disabled {
  color: #94a3b8 !important;
  cursor: not-allowed;
  text-decoration: none;
}
.material-operation-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

.drawer-body { padding: 16px; font-size: 12px; }
.trace-actions { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
.trace-statuses { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
.trace-status-label { display:inline-block; margin-right:6px; }
.material-section-title { flex:none; font-size:13px; }
.kit-material-workbench ::v-deep .material-table td.ord-suite-status-pending,
.kit-material-workbench ::v-deep .material-table td.ord-suite-status-pending span,
.kit-material-workbench ::v-deep .material-table td.ord-suite-status-pending div,
.drawer-body ::v-deep .ord-suite-status-pending td,
.ord-suite-status-pending { color:#909399!important; }

.kit-material-workbench ::v-deep .material-table td.table-body-cell-blue,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-blue span,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-blue div,
.drawer-body ::v-deep .table-body-cell-blue td,
.table-body-cell-blue { color:#409eff!important; }

.kit-material-workbench ::v-deep .material-table td.table-body-cell-green,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-green span,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-green div,
.drawer-body ::v-deep .table-body-cell-green td,
.table-body-cell-green { color:#67c23a!important; }

.kit-material-workbench ::v-deep .material-table td.table-body-cell-yellow,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-yellow span,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-yellow div,
.drawer-body ::v-deep .table-body-cell-yellow td,
.table-body-cell-yellow { color:#e6a23c!important; }

.kit-material-workbench ::v-deep .material-table td.table-body-cell-red,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-red span,
.kit-material-workbench ::v-deep .material-table td.table-body-cell-red div,
.drawer-body ::v-deep .table-body-cell-red td,
.table-body-cell-red { color:#f56c6c!important; }
.status-unknown { color:#909399; }
.material-state-filter { flex-wrap:wrap; }
.material-workbar { flex-wrap:wrap; }
.material-quick-search { width:220px; max-width:100%; flex:0 1 220px; }
.material-section-title { white-space:nowrap; }
.drawer-section { margin-bottom: 20px; }
.drawer-heading { font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 8px; }




.kit-material-workbench ::v-deep .summary-header-row {
  width: 100%;
  box-sizing: border-box;
}

.kit-material-workbench ::v-deep .summary-header-title {
  height: 25px;
  line-height: 25px;
  padding: 0 6px;
  font-weight: var(--font-weight-table-header, 600);
  font-size: var(--font-size-table-header, 12px);
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-bottom: 1px solid #dcdfe6;
  background: #fff;
  cursor: default;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.kit-material-workbench ::v-deep .summary-header-search {
  height: 25px;
  display: flex;
  align-items: center;
  padding: 0;
  background: #f8fafc;
  box-sizing: border-box;
}

.kit-material-workbench ::v-deep .summary-header-filter {
  display: block;
  width: 100% !important;
  height: 21px;
  margin: 0;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__inner {
  height: 21px;
  line-height: 21px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 2px;
  padding: 0 16px 0 4px;
  font-size: 12px;
  font-weight: 400;
  box-sizing: border-box;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__inner:hover,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__inner:focus {
  border-color: #409eff;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__prefix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__suffix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__icon {
  height: 21px;
  line-height: 21px;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__icon {
  width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: top;
  line-height: 1;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__prefix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__suffix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__suffix-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  top: 0;
  height: 21px;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__icon::after {
  display: none;
}

.kit-material-workbench ::v-deep .summary-header-filter.el-date-editor .el-input__inner {
  padding-left: 17px;
}

/* 上表查询控件铺满查询行，保留文字和清空/下拉图标的内部间距。 */
.kit-material-workbench ::v-deep .summary-header-filter,
.kit-material-workbench ::v-deep .summary-header-filter .el-input,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__inner,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__prefix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__suffix,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__suffix-inner,
.kit-material-workbench ::v-deep .summary-header-filter .el-input__icon {
  height: 25px;
  line-height: 25px;
}

.kit-material-workbench ::v-deep .summary-header-filter .el-input__inner {
  border-radius: 0;
}

.kit-material-workbench ::v-deep .summary-header-filter input[type="number"] {
  -moz-appearance: textfield;
}

.kit-material-workbench ::v-deep .summary-header-filter input::-webkit-inner-spin-button,
.kit-material-workbench ::v-deep .summary-header-filter input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.kit-material-workbench ::v-deep .summary-header-filter.el-input-number .el-input-number__decrease,
.kit-material-workbench ::v-deep .summary-header-filter.el-input-number .el-input-number__increase {
  display: none;
}

.kit-material-workbench ::v-deep .summary-header-filter.el-input-number .el-input__inner {
  padding: 0 3px;
  text-align: left;
}

.kit-material-workbench ::v-deep .el-input__inner,
.kit-material-workbench ::v-deep .el-textarea__inner,
.kit-material-workbench ::v-deep .el-form-item__label,
.kit-material-workbench ::v-deep .el-radio__label,
.kit-material-workbench ::v-deep .el-checkbox__label {
  color: var(--foreground);
  font-size: var(--font-size-body);
}

.kit-material-workbench ::v-deep .el-input__inner::placeholder,
.kit-material-workbench ::v-deep .el-textarea__inner::placeholder {
  color: var(--muted-foreground);
  font-size: var(--font-size-hint);
  opacity: 1;
}

.kit-material-workbench ::v-deep .summary-filter-reset.el-button {
  position: static;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin: 0 auto;
  padding: 0;
  color: var(--muted-foreground);
  background: transparent;
  border: 0;
  line-height: 16px;
}
.kit-material-workbench ::v-deep .summary-filter-reset.el-button:hover,
.kit-material-workbench ::v-deep .summary-filter-reset.el-button:focus {
  color: #f56c6c;
  background: transparent;
}

.kit-material-workbench ::v-deep .material-ve-header-title {
  height: 24px;
  line-height: 24px;
  padding: 0 4px;
  font-weight: var(--font-weight-table-header, 600);
  font-size: var(--font-size-table-header, 12px);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.kit-material-workbench ::v-deep .table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.kit-material-workbench ::v-deep .ve-table {
  height: 100% !important;
  max-height: 100% !important;
}

.kit-material-workbench ::v-deep .ve-table-container {
  height: 100% !important;
  max-height: 100% !important;
}

.kit-material-workbench ::v-deep .ve-table-content-wrapper {
  min-height: 100%;
}


/* summary-table 与 material-table 锁定网格边框 #dcdfe6 */
.kit-material-workbench ::v-deep .summary-table.ve-table-border-around,
.kit-material-workbench ::v-deep .material-table.ve-table-border-around {
  border: 1px solid #dcdfe6 !important;
}
.kit-material-workbench ::v-deep .summary-table.ve-table-border-x .ve-table-header-tr th,
.kit-material-workbench ::v-deep .summary-table.ve-table-border-x .ve-table-body-tr td,
.kit-material-workbench ::v-deep .material-table.ve-table-border-x .ve-table-header-tr th,
.kit-material-workbench ::v-deep .material-table.ve-table-border-x .ve-table-body-tr td {
  border-right: 1px solid #dcdfe6 !important;
}
.kit-material-workbench ::v-deep .summary-table.ve-table-border-y .ve-table-header-tr th,
.kit-material-workbench ::v-deep .summary-table.ve-table-border-y .ve-table-body-tr td,
.kit-material-workbench ::v-deep .material-table.ve-table-border-y .ve-table-header-tr th,
.kit-material-workbench ::v-deep .material-table.ve-table-border-y .ve-table-body-tr td {
  border-bottom: 1px solid #dcdfe6 !important;
}
.kit-material-workbench ::v-deep .summary-table .ve-table-fixed-left,
.kit-material-workbench ::v-deep .summary-table .ve-table-fixed-right,
.kit-material-workbench ::v-deep .material-table .ve-table-fixed-left,
.kit-material-workbench ::v-deep .material-table .ve-table-fixed-right {
  background: #fff;
}

/* 上表表头高度 50px */
.kit-material-workbench ::v-deep .summary-table .ve-table-header-tr th {
  height: 50px !important;
  padding: 0 !important;
  background: #fff !important;
  vertical-align: top !important;
}
.kit-material-workbench ::v-deep .summary-table .ve-table-header-th-cell {
  height: 50px !important;
  padding: 0 !important;
  line-height: normal !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: stretch !important;
  box-sizing: border-box !important;
}
.kit-material-workbench ::v-deep .summary-table .summary-header-container {
  height: 50px;
  display: flex;
  flex-direction: column;
}
.kit-material-workbench ::v-deep .summary-table .summary-reset-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kit-material-workbench ::v-deep .summary-table .summary-reset-header .summary-filter-reset {
  width: 28px;
  height: 28px;
  margin: 0;
  line-height: 1;
}
.kit-material-workbench ::v-deep .summary-table .summary-reset-header .el-icon-delete-solid {
  display: block;
  width: 18px;
  height: 18px;
  font-size: 18px;
  line-height: 18px;
}

/* 下表单层表头：高度 32px */
.kit-material-workbench ::v-deep .material-table .ve-table-header-tr th {
  height: 32px !important;
  padding: 0 !important;
  background: #f8fafc !important;
  vertical-align: middle !important;
}
.kit-material-workbench ::v-deep .material-table .ve-table-header-th-cell {
  height: 32px !important;
  line-height: 32px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  box-sizing: border-box !important;
}

.kit-material-workbench ::v-deep .summary-header-title * {
  pointer-events: none !important;
}
.kit-material-workbench ::v-deep .material-ve-header-title,
.kit-material-workbench ::v-deep .material-ve-header-title * {
  pointer-events: none !important;
}

/* 强化列宽拖拽手柄宽度与层级，大幅提升拖拽抓取灵敏度 */
.kit-material-workbench ::v-deep .ve-table-column-resizer-handler {
  width: 10px !important;
  z-index: 100 !important;
  cursor: col-resize !important;
}
.kit-material-workbench ::v-deep .ve-table-column-resizer-line {
  z-index: 101 !important;
}

/* 单元格行高 32px */
.kit-material-workbench ::v-deep .summary-table .ve-table-body-tr,
.kit-material-workbench ::v-deep .material-table .ve-table-body-tr {
  height: 32px !important;
}
.kit-material-workbench ::v-deep .summary-table .ve-table-body-tr td,
.kit-material-workbench ::v-deep .material-table .ve-table-body-tr td {
  height: 31px !important;
  padding: 0 8px !important;
  font-size: 12px !important;
  color: var(--foreground);
}
.kit-material-workbench ::v-deep .summary-table .ve-table-body-td-cell,
.kit-material-workbench ::v-deep .material-table .ve-table-body-td-cell {
  height: 31px;
  line-height: 31px;
  padding: 0;
  box-sizing: border-box;
}

.kit-material-workbench ::v-deep .ve-table-body-td-span-ellipsis {
  display: block !important;
  width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  word-break: break-all;
}

.material-empty-state {
  position: absolute;
  top: 32px;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #909399;
  font-size: 12px;
  pointer-events: none;
}
.material-empty-state .el-icon-document {
  font-size: 32px;
  color: #c0c4cc;
}

/* 齐套% 进度条样式 */
.kit-material-workbench ::v-deep .summary-progress-cell {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
}
.kit-material-workbench ::v-deep .summary-progress-track {
  position: relative !important;
  width: 100% !important;
  height: 16px !important;
  background-color: #e2e8f0 !important;
  border-radius: 8px !important;
  overflow: hidden !important;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06) !important;
}
.kit-material-workbench ::v-deep .summary-progress-bar {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  height: 100% !important;
  border-radius: 8px !important;
  transition: width 0.3s ease !important;
}
.kit-material-workbench ::v-deep .summary-progress-text {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  pointer-events: none !important;
  user-select: none !important;
}
</style>

<style>
.suite-remark-dialog { border-radius: 12px; overflow: hidden; max-height: 86vh; display: flex; flex-direction: column; }
.suite-remark-dialog .el-dialog__header { padding: 22px 24px 18px; border-bottom: 1px solid #e8edf3; }
.suite-remark-dialog .el-dialog__title { font-size: 18px; font-weight: 600; color: #182230; }
.suite-remark-dialog .el-dialog__body { padding: 20px 24px; overflow: auto; }
.suite-remark-dialog .el-dialog__footer { padding: 12px 24px; border-top: 1px solid #e8edf3; }
.suite-remark-material { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; padding: 16px; background: #f5f8fc; border-radius: 8px; color: #334155; }
.suite-remark-code { font-family: monospace; font-size: 15px; color: #2563eb; }
.suite-remark-section { display: flex; justify-content: space-between; padding: 22px 0 12px; font-size: 13px; font-weight: 600; color: #475569; }
.suite-remark-section > span:last-child { font-weight: 400; color: #94a3b8; }
.suite-remark-content { min-height: 100px; }
.suite-remark-dialog .el-table { border-color: #dce4ed; }
.suite-remark-dialog .el-table th { height: 44px; padding: 10px 0; background: #f8fafc; color: #475569; font-weight: 600; }
.suite-remark-dialog .el-table th > .cell { font-size: 14px; line-height: 24px; }
.suite-remark-dialog .el-table th.is-leaf,
.suite-remark-dialog .el-table td { border-color: #dce4ed; }
.suite-remark-dialog .el-table--border::after,
.suite-remark-dialog .el-table::before { background-color: #dce4ed; }
.suite-remark-value { display: block; white-space: pre-wrap; overflow-wrap: anywhere; padding: 7px 0; color: #64748b; line-height: 1.7; }
.suite-remark-note { color: #1e293b; }
</style>
